import React, { useState, useEffect } from 'react';
import { Dropdown, Modal, Select, notification } from 'antd';
import { connect, useDispatch } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';

import { togglePortfolioModal } from '../../../../actions/modalAction';
import {
  addPortfolioMembers,
  addProjectFromPortfolio,
  createPortfolio,
  getPortfolios,
  removePortfolioMember,
  updateProjectFromPortfolio,
} from '../../../../actions/WorkspaceActions';

const SectionLabel = ({ children }) => (
  <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">{children}</div>
);

const PortfolioModal = ({
  isModalOpen,
  projects: modalProjects,
  currentWorkspace,
  workspacePortfolios,
  workspaceMembers,
  selectedPortfolio,
}) => {
  const dispatch = useDispatch();
  const intl = useIntl();

  const { 2: portfolioId } = window.location.pathname.split('/');

  const [name, setName] = useState('');
  const [error, setError] = useState(false);
  const [isPrivate, setIsPrivate] = useState(true);
  const [portfolioProjects, setPortfolioProjects] = useState([]);
  const [projects, setProjects] = useState(modalProjects);
  const [portfolio, setPortfolio] = useState();
  // Unified member selection: holds IDs of all currently-selected members (existing + newly added)
  const [selectedMemberIds, setSelectedMemberIds] = useState([]);
  // Snapshot of participants when modal opened — used to diff on save
  const [originalMemberIds, setOriginalMemberIds] = useState([]);

  // Seed form from Redux when modal opens (selectedPortfolio is already loaded by the details page)
  useEffect(() => {
    if (!isModalOpen) return;

    let source = null;
    if (portfolioId) {
      source = (selectedPortfolio?._id == portfolioId ? selectedPortfolio : null)
        ?? workspacePortfolios?.find(p => p?._id == portfolioId);
    }

    if (source) {
      setPortfolio(source);
      setName(source.name || '');
      setIsPrivate(source.access !== 'public');
      if (source.projects?.length > 0) {
        setProjects(modalProjects?.filter(p => !source.projects.find(pp => pp?._id == p?._id)));
      } else {
        setProjects(modalProjects);
      }
      const ids = (source.participants || []).map(p => p._id?.toString());
      setSelectedMemberIds(ids);
      setOriginalMemberIds(ids);
    } else if (!portfolioId) {
      setPortfolio(undefined);
      setName('');
      setIsPrivate(true);
      setProjects(modalProjects);
      setSelectedMemberIds([]);
      setOriginalMemberIds([]);
    }
  }, [isModalOpen, portfolioId]);

  // If portfolios weren't loaded yet, fetch and resolve
  useEffect(() => {
    if (!portfolioId || portfolio) return;
    const init = async () => {
      const list = workspacePortfolios?.length > 0 ? workspacePortfolios : await dispatch(getPortfolios());
      const found = list?.find(p => p?._id == portfolioId);
      if (found) {
        setPortfolio(found);
        setName(found.name || '');
        setIsPrivate(found.access !== 'public');
        const ids = (found.participants || []).map(p => p._id?.toString());
        setSelectedMemberIds(ids);
        setOriginalMemberIds(ids);
        if (found.projects?.length > 0) {
          setProjects(modalProjects?.filter(p => !found.projects.find(pp => pp?._id == p?._id)));
        }
      }
    };
    init();
  }, [portfolioId]);

  // Keep participants in sync after add/remove operations complete
  useEffect(() => {
    if (portfolioId && selectedPortfolio?._id == portfolioId) {
      setPortfolio(prev => prev ? { ...prev, participants: selectedPortfolio.participants } : prev);
      const ids = (selectedPortfolio.participants || []).map(p => p._id?.toString());
      setSelectedMemberIds(ids);
      setOriginalMemberIds(ids);
    }
  }, [selectedPortfolio?.participants]);

  const closeModal = () => {
    dispatch(togglePortfolioModal(false));
    setPortfolioProjects([]);
    setSelectedMemberIds([]);
    setOriginalMemberIds([]);
    setName('');
  };

  // Build a lookup map of all workspace members by ID for display
  const memberById = Object.fromEntries((workspaceMembers || []).map(m => [m._id?.toString(), m]));

  // Admin IDs that cannot be removed
  const adminIds = new Set(
    (portfolio?.participants || []).filter(p => p.role === 'admin').map(p => p._id?.toString())
  );

  const handleSave = async () => {
    if (!name && !portfolioId) {
      setError(true);
      return;
    }
    setError(false);

    if (portfolioId) {
      await dispatch(updateProjectFromPortfolio(portfolioId, {
        access: isPrivate ? 'private' : 'public',
        name,
      }));

      if (portfolioProjects.length) {
        await dispatch(addProjectFromPortfolio(portfolioId, portfolioProjects));
        setProjects(prev => prev.filter(p => !portfolioProjects.some(pp => pp._id === p._id)));
      }

      // Diff selected vs original to get adds and removes
      const toAdd = selectedMemberIds.filter(id => !originalMemberIds.includes(id));
      const toRemove = originalMemberIds.filter(id => !selectedMemberIds.includes(id) && !adminIds.has(id));

      if (toAdd.length) {
        const usersToAdd = toAdd.map(id => {
          const m = memberById[id];
          return { userId: id, fullName: m?.fullName, avi: m?.avi, email: m?.email, role: 'viewer' };
        });
        await dispatch(addPortfolioMembers(portfolioId, usersToAdd));
      }

      for (const userId of toRemove) {
        await dispatch(removePortfolioMember(portfolioId, userId));
      }

      setPortfolio(prev => ({
        ...prev,
        access: isPrivate ? 'private' : 'public',
        name,
        projects: [...(prev?.projects || []), ...portfolioProjects],
      }));

      const changes = [];
      if (portfolioProjects.length) changes.push(`${portfolioProjects.length} project${portfolioProjects.length > 1 ? 's' : ''} added`);
      if (toAdd.length) changes.push(`${toAdd.length} member${toAdd.length > 1 ? 's' : ''} added`);
      if (toRemove.length) changes.push(`${toRemove.length} member${toRemove.length > 1 ? 's' : ''} removed`);

      notification.success({
        message: 'Portfolio updated',
        description: changes.length ? changes.join(', ') : undefined,
        placement: 'bottomRight',
      });
    } else {
      const usersToCreate = selectedMemberIds.map(id => {
        const m = memberById[id];
        return { userId: id, fullName: m?.fullName, avi: m?.avi, email: m?.email, role: 'viewer' };
      });
      dispatch(createPortfolio({
        workspace: currentWorkspace?._id,
        name,
        description: '',
        projects: portfolioProjects,
        access: isPrivate ? 'private' : 'public',
        users: usersToCreate,
      }));
    }

    closeModal();
  };

  const isEditing = !!portfolioId;

  return (
    <Modal
      title={
        <span className="text-base font-semibold text-gray-800">
          {isEditing
            ? intl.formatMessage({ id: 'EDIT_PORTFOLIO', defaultMessage: 'Edit Portfolio' })
            : intl.formatMessage({ id: 'PORTFOLIO', defaultMessage: 'New Portfolio' })}
        </span>
      }
      open={isModalOpen}
      onCancel={closeModal}
      okButtonProps={{ style: { display: 'none' } }}
      cancelButtonProps={{ style: { display: 'none' } }}
      className="custom-modal"
      styles={{
        header: { padding: '16px 24px', marginBottom: 0, borderBottom: '1px solid #f0f0f0' },
        body: { padding: '0 24px' },
        footer: { padding: '12px 24px', borderTop: '1px solid #f0f0f0', marginTop: 0 },
      }}
      footer={
        <div className="flex justify-end gap-2 py-1">
          <button
            onClick={closeModal}
            className="px-4 py-1.5 rounded-lg border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-sm text-white font-medium transition-colors"
          >
            {!isEditing && (
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
              </svg>
            )}
            <FormattedMessage
              id={isEditing ? 'EDIT_PORTFOLIO' : 'ADD_PORTFOLIO'}
              defaultMessage={isEditing ? 'Save changes' : 'Create portfolio'}
            />
          </button>
        </div>
      }
    >
      <div className="flex flex-col gap-4 py-4 px-1">
        {/* Name */}
        <div>
          <SectionLabel>Portfolio name</SectionLabel>
          <input
            value={name}
            onChange={e => { setName(e.target.value); setError(false); }}
            placeholder="e.g. Q3 Product Initiatives"
            className={`w-full border rounded-lg px-3 py-2 text-sm text-gray-800 placeholder-gray-400 outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition ${error ? 'border-red-400 ring-1 ring-red-300' : 'border-gray-300'}`}
          />
          {error && <p className="mt-1 text-xs text-red-400">Name cannot be blank</p>}
        </div>

        {/* Access level */}
        <div>
          <SectionLabel>Access level</SectionLabel>
          <Dropdown
            trigger={['click']}
            menu={{
              items: [
                {
                  key: 'private',
                  label: (
                    <div className="flex items-center gap-2">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                      </svg>
                      <span>Private — only visible to members</span>
                    </div>
                  ),
                  onClick: () => setIsPrivate(true),
                },
                {
                  key: 'public',
                  label: (
                    <div className="flex items-center gap-2">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10" />
                        <line x1="2" y1="12" x2="22" y2="12" />
                        <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                      </svg>
                      <span>Public — visible to entire workspace</span>
                    </div>
                  ),
                  onClick: () => setIsPrivate(false),
                },
              ],
            }}
            placement="bottom"
          >
            <button className="w-full flex items-center justify-between border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 hover:border-blue-400 focus:outline-none transition">
              <span className="flex items-center gap-2">
                {isPrivate ? (
                  <>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                    </svg>
                    Private — only visible to members
                  </>
                ) : (
                  <>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10" />
                      <line x1="2" y1="12" x2="22" y2="12" />
                      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                    </svg>
                    Public — visible to entire workspace
                  </>
                )}
              </span>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>
          </Dropdown>
        </div>

        {/* Members (private only) */}
        {isPrivate && (
          <div>
            <SectionLabel>Members</SectionLabel>
            <Select
              mode="multiple"
              style={{ width: '100%' }}
              placeholder="Select workspace members…"
              suffixIcon={false}
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              options={(workspaceMembers || []).map(m => ({
                value: m._id?.toString(),
                label: m.fullName || m.email,
                disabled: adminIds.has(m._id?.toString()),
              }))}
              value={selectedMemberIds}
              onChange={ids => setSelectedMemberIds(ids)}
              tagRender={({ label, value, closable, onClose }) => (
                <span className="inline-flex items-center gap-1 bg-gray-100 rounded px-2 py-0.5 mr-1 my-0.5 text-sm text-gray-700">
                  <span className="w-4 h-4 rounded-full bg-blue-500 inline-flex items-center justify-center text-white text-[9px] font-bold flex-shrink-0">
                    {(memberById[value]?.fullName || memberById[value]?.email || '?')[0].toUpperCase()}
                  </span>
                  {label}
                  {closable && !adminIds.has(value) && (
                    <span
                      onClick={onClose}
                      className="ml-0.5 text-gray-400 hover:text-gray-600 cursor-pointer leading-none"
                    >
                      ×
                    </span>
                  )}
                </span>
              )}
              notFoundContent={<span className="text-xs text-gray-400">No workspace members available</span>}
            />
          </div>
        )}

        {/* Projects */}
        <div>
          <SectionLabel>Add projects <span className="text-gray-400 normal-case font-normal">(optional)</span></SectionLabel>
          <Select
            mode="multiple"
            style={{ width: '100%' }}
            placeholder="Search projects…"
            suffixIcon={false}
            options={projects?.map(project => ({
              value: project?._id,
              label: project?.title,
            }))}
            value={portfolioProjects.map(p => p._id)}
            onChange={selectedIds => {
              setPortfolioProjects(projects.filter(p => selectedIds.includes(p._id)));
            }}
            notFoundContent={<span className="text-xs text-gray-400">Join or create projects to see them here</span>}
          />
        </div>
      </div>
    </Modal>
  );
};

const mapStateToProps = (state) => ({
  currentWorkspace: state?.workspaceReducer?.currentWorkspace,
  workspacePortfolios: state.workspaceReducer.workspacePortfolios,
  workspaceMembers: state.workspaceReducer.currentWorkspace?.members,
  selectedPortfolio: state.workspaceReducer.selectedPortfolio,
  project: state.projectReducer.myProjects,
});

export default connect(mapStateToProps)(PortfolioModal);
